import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { recordCallback } from './api';

// Landing route for Zernio's HEADLESS OAuth redirect. Two observed shapes:
//   DIRECT (facebook):   ?onboardingId=X&profileId=…&tempToken=…&userProfile=…
//   pendingDataToken:    ?onboardingId=X&pendingDataToken=…
// The signed-in SPA forwards the params to the authed callback (a browser
// redirect can't carry a Firebase ID token, so the handshake happens server-
// side from here), which resolves the token + fetches the page list, then
// returns to the wizard at the page-selection step. We POST the params in the
// request body — the tempToken must not be repeated in a URL on our own hop.
//
// Tolerates a malformed double-'?' (a naive provider appending its params
// onto a redirect_url that already has a query string).
export function parseCallbackSearch(search: string): URLSearchParams {
  return new URLSearchParams(search.replace(/^\?/, '').replace(/\?/g, '&'));
}

export function OnboardingCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard
    ran.current = true;

    const params = parseCallbackSearch(window.location.search);
    const onboardingId = params.get('onboardingId');
    const tempToken = params.get('tempToken');
    const pendingDataToken = params.get('pendingDataToken');
    if (!onboardingId || (!tempToken && !pendingDataToken)) {
      setError(
        'The Meta connect redirect was missing its parameters. Go back to the wizard and retry the connect step.',
      );
      return;
    }
    recordCallback({
      onboardingId,
      tempToken: tempToken ?? undefined,
      pendingDataToken: pendingDataToken ?? undefined,
      profileId: params.get('profileId') ?? undefined,
      userProfile: params.get('userProfile') ?? undefined,
      expiresIn: params.get('expiresIn') ? Number(params.get('expiresIn')) : undefined,
    })
      .then(({ onboarding }) => {
        navigate(`/onboarding?focus=${onboarding.id}`, { replace: true });
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to record the Meta connection.');
      });
  }, [navigate]);

  return (
    <div className="flex flex-col items-center gap-4 py-24">
      {error ? (
        <>
          <p className="max-w-md text-center text-sm text-rose-600">{error}</p>
          <Button variant="outline" onClick={() => navigate('/onboarding')}>
            Back to onboarding
          </Button>
        </>
      ) : (
        <>
          <Spinner />
          <p className="text-sm text-slate-500">Fetching your Facebook pages…</p>
        </>
      )}
    </div>
  );
}
